import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  StateStoring,
  Summary,
  TotalItem,
} from 'devextreme-react/data-grid';

import { Template } from 'devextreme-react';
import { maskedPhone } from 'utils/masked-phone';

import { useLazyGetDispatcherExcelReportQuery } from 'bus/request';

import { shiftCurrent } from '../../../../bus/shift/actions';
import { AppCard, AppLayoutTop } from '../../../../components/appComponents';
import ClaimsProvider from '../../../../components/claimsBlock';
import { useAuth } from '../../../../context/auth';
import $createStore from '../../../../utils/create-store-instance';
import { CreateButton, ModalConfirmShift, ModalReportExcel, TaskRowTemplate } from './components';

const TaskDetail: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { getClaims }: any = useAuth();

  const [modalIsVisibleReport, setModalIsVisibleReport] = useState(false);

  const [getDispatcherExcelReport] = useLazyGetDispatcherExcelReportQuery();

  const requestDateSource = useMemo(() => {
    return $createStore('/v1/Request', 'id');
  }, []);
  const refGrid = useRef<DataGrid | null>(null);

  const init = useCallback(() => {
    dispatch(shiftCurrent.async());
  }, [dispatch]);

  useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const customSummaries = (options: any) => {
    if (options.name === 'SelectedRowsSummary') {
      if (options.summaryProcess === 'start') {
        options.totalValue = 0;
      } else if (options.summaryProcess === 'calculate') {
        options.totalValue += options.value.quantity - options.value.shipped;
        options.totalValue = Math.round(options.totalValue * 100) / 100;
      }
    }
  };

  const refresh = useCallback(() => refGrid.current?.instance.refresh(), []);

  const downloadExcel = () => {
    setModalIsVisibleReport(true);
  };

  const downloadReportTable = async () => {
    const filtering = refGrid.current?.instance.getCombinedFilter();
    await getDispatcherExcelReport(JSON.stringify(filtering));
  };

  return (
    <div className="container-dev">
      <AppLayoutTop title="Заявки">
        <div className="d-flex">
          <Button className="send mr-2" icon="refresh" onClick={refresh} />
          <Button className="send mr-2" icon="export" onClick={downloadExcel} hint="Отчет" />
          <ClaimsProvider claimsBlock={['Shift']}>
            <ModalConfirmShift />
          </ClaimsProvider>
          <CreateButton />
        </div>
      </AppLayoutTop>

      <ModalReportExcel changeVisible={setModalIsVisibleReport} visible={modalIsVisibleReport} />

      <AppCard>
        <DataGrid
          dataSource={requestDateSource as any}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={{
            filtering: true,
            grouping: false,
            groupPaging: false,
            paging: false,
            sorting: true,
            summary: false,
          }}
          rowRender={(data) => <TaskRowTemplate data={data.data} claims={getClaims(['CanLookPrice'])} />}
          onRowDblClick={(e) => history.push(`/task/detail/${e.data.id}`)}
          onToolbarPreparing={(e) => {
            e.toolbarOptions?.items?.unshift({
              location: 'after',
              template: 'xlsfile',
            });
          }}
        >
          <Template name="xlsfile">
            <Button icon="xlsfile" onClick={downloadReportTable} />
          </Template>

          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible />

          <StateStoring enabled={true} type="localStorage" storageKey="requestListStorKey2" />

          <Column
            dataField="requestStatus"
            caption=""
            width={50}
            allowFiltering={false}
            calculateSortValue="requestStatus.Status"
          />
          <Column dataField="id" caption="Номер" width={80} />
          <Column dataField="date" caption="Время" width={80} dataType="date" />
          <Column dataField="object" caption="Объект" />
          <Column dataField="address" caption="Адрес" />
          <Column dataField="phone" caption="Телефон" width="120" cellRender={({ data }) => maskedPhone(data.phone)} />
          <Column dataField="nomenclatureName" caption="Марка (Плотность)" width="110" />
          <Column dataField="contractorPump" caption="Метод приема" width="150" alignment="center" />

          <Column dataField="comment" caption="Комментарий" />
          <Column dataField="design" caption="Конструкция" />

          <Column dataField="quantity" caption="Объём" dataType="number" width={100} />
          <Column dataField="shipped" caption="Отлито" dataType="number" width={100} />
          <Column dataField="customColumn" dataType="number" caption="Осталось" width={100} />

          <Summary calculateCustomSummary={customSummaries}>
            <TotalItem column="comment" summaryType="sum" displayFormat="Итого: " alignment="right" />
            <TotalItem column="quantity" summaryType="sum" displayFormat="{0}" />
            <TotalItem column="shipped" summaryType="sum" displayFormat="{0}" />
            <TotalItem
              name="SelectedRowsSummary"
              summaryType="custom"
              displayFormat="{0}"
              showInColumn="customColumn"
            />
          </Summary>

          <Paging defaultPageSize={100} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default TaskDetail;
